import React from "react";
import { Link } from "react-router-dom";
import { useBilling } from "@/hooks/useBilling";
import { Sparkles, AlertTriangle } from "lucide-react";

/** Small badge shown in the sidebar near the user's info. */
export function PlanChip() {
  const { subscription, per_quote_credits, daysLeft, loading } = useBilling();
  if (loading) return null;

  if (!subscription || subscription.status !== "active") {
    if (per_quote_credits > 0) {
      return (
        <Link
          to="/pricing"
          data-testid="plan-chip-credits"
          className="block w-full text-xs text-slate-600 hover:text-slate-900 mt-2 px-3 py-2 rounded-md bg-slate-50 border border-slate-200"
        >
          <span className="font-medium">{per_quote_credits}</span> quote credit{per_quote_credits === 1 ? "" : "s"} · <span className="text-orange-600">Upgrade</span>
        </Link>
      );
    }
    return (
      <Link
        to="/pricing"
        data-testid="plan-chip-free"
        className="block w-full text-xs mt-2 px-3 py-2 rounded-md bg-orange-50 border border-orange-200 text-orange-700 hover:bg-orange-100"
      >
        <Sparkles className="h-3 w-3 inline mr-1" /> Upgrade plan
      </Link>
    );
  }

  const expiringSoon = daysLeft !== null && daysLeft <= 3;
  return (
    <Link
      to="/pricing"
      data-testid="plan-chip-active"
      className={`block w-full text-xs mt-2 px-3 py-2 rounded-md border ${
        expiringSoon
          ? "bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"
          : "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"
      }`}
    >
      <span className="font-medium">{subscription.plan_name}</span> ·{" "}
      {daysLeft !== null ? `${daysLeft} day${daysLeft === 1 ? "" : "s"} left` : "active"}
    </Link>
  );
}

/** Top-of-dashboard banner shown when subscription expires within 3 days. */
export function RenewalBanner() {
  const { subscription, daysLeft, expiringSoon, loading } = useBilling();
  if (loading || !expiringSoon || !subscription) return null;

  return (
    <div
      className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center justify-between gap-4"
      data-testid="renewal-banner"
    >
      <div className="flex items-start gap-3 text-amber-900">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="text-sm">
          <div className="font-semibold">Your {subscription.plan_name} plan renews in {daysLeft} day{daysLeft === 1 ? "" : "s"}.</div>
          <div className="text-amber-800/80">Keep unlimited quotes flowing — extend now to avoid interruption.</div>
        </div>
      </div>
      <Link
        to="/pricing"
        className="text-sm font-medium rounded-md px-3 py-2 bg-amber-600 hover:bg-amber-700 text-white whitespace-nowrap"
        data-testid="renewal-banner-cta"
      >
        Renew now
      </Link>
    </div>
  );
}
